import { Box, Chip, CircularProgress, Paper, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import BlockContainer from '../../core/components/layout/BlockContainer'
import { CreditCard, Download, List, ListAlt, SearchOutlined } from '@mui/icons-material'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { extranalColor } from '../../core/theme/mindright';
import { Button } from '../../core/components';
import { useGetGetTransactionHistoryQuery, useGetStudentSubscriptionQuery } from './api/billing.api';
import { BillingHistory } from './types/types';
import { LoaderIcon } from 'react-hot-toast';
import { Button as MUIButton } from '@mui/material'
import moment from 'moment';
import { generateJSONToCSV } from '../../services/csvService';

export const TypeChip = ({ label }: { label: string }) => {

    const getColor = (type: string) => {
        switch (type?.toLocaleLowerCase()) {
            case 'subscription':
                return {
                    bg: extranalColor.lightBlue,
                    color: extranalColor.blue
                }
            case 'auto-recharge':
            case 'completed':
            case 'active':
                return {
                    bg: extranalColor.lightGreen,
                    color: extranalColor.green
                }
            case 'pending':
                return {
                    bg: extranalColor.lightWarning,
                    color: extranalColor.warning
                }
            case 'error':
            case 'cancel':
                case 'canceled':
                return {
                    bg: extranalColor.lightError,
                    color: extranalColor.error
                }
        }
    }
    return <Chip label={label || 'N/A'}
        size='small'
        sx={{
            background: getColor(label)?.bg,
            color: getColor(label)?.color,
            textTransform: 'capitalize',
            fontWeight: '500'
        }}
    />
}
const TRIAL_MOMENTS_LABEL = '15 MindRight Moments';

/** Trial transaction: $0 amount and either (a) user is in trial per subscription API, or (b) realAmount present (e.g. $50) */
function isTrialTransaction(transaction: BillingHistory, isInTrial?: boolean): boolean {
    if (transaction?.amount !== 0) return false;
    return (
        isInTrial === true ||
        (transaction?.realAmount != null && transaction.realAmount > 0)
    );
}

const SearchBar = ({ onSearch }: { onSearch: (arg: string) => void; }) => {
    const theme = useTheme()
    return <Stack direction={'row'}
        border={1}
        borderColor={theme.palette.divider}
        padding={0}
        borderRadius={2}
        alignItems={'center'}
        gap={1}
        paddingLeft={2}
        height={40}
        sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
                flexGrow: 1,
                mb: 3
            },
            flexGrow: 0.2,
        })}

    >
        <SearchOutlined fontSize='medium' sx={{ color: theme.palette.grey[500] }} />
        <input style={{ width: '100%', fontSize: 14 }} placeholder='Search transactions...'
            onChange={(event) => onSearch && onSearch(event.target.value)}
        />
    </Stack>
}
const DownloadCSVButton = ({ onClick, isLoading }: { onClick: () => void; isLoading: boolean }) => {
    const theme = useTheme();
    return <MUIButton startIcon={<Download />}
        onClick={() => onClick && onClick()}
        variant='outlined'
        disabled={isLoading}
        loading={isLoading}
        sx={{
            marginLeft: 'auto',
            borderColor: theme.palette.secondary[600],
            color: theme.palette.secondary[600],
            padding: '7px 30px',
            '&:hover': {
                borderColor: theme.palette.secondary[500],
                background: theme.palette.secondary[500],
                color: '#fff',
            }
        }}
    >
        Download CSV
    </MUIButton>
}
const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme.palette.grey[500],
    },
}));
const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.body}`]: {
        color: theme.palette.grey[800],
        [theme.breakpoints.down('md')]: {
            fontWeight: '500',
        },
        [theme.breakpoints.down('sm')]: {
            fontWeight: '600',
            fontSize: '16px!important',
            padding: '10px'
        },
    },
}));
const TableCard = ({ data, isInTrial }: { data: BillingHistory; isInTrial?: boolean }) => {
    const theme = useTheme()
    const isTrial = isTrialTransaction(data, isInTrial);
    return <Box sx={{ mb: 3, border: 1, borderRadius: 3, borderColor: theme.palette.divider }}>
        <Table aria-label="simple table">
            <TableBody>
                <TableRow>
                    <StyledTableHeadCell component="th" >Date</StyledTableHeadCell>
                    <StyledTableBodyCell>
                        {moment(data.createdAt).format("MMM, DD YYYY")}
                    </StyledTableBodyCell>
                </TableRow>
                <TableRow>
                    <StyledTableHeadCell component="th" >Description</StyledTableHeadCell>
                    <StyledTableBodyCell>
                        <Typography variant='body1' sx={{ color: theme.palette.grey[800], fontWeight: 600, fontSize: theme.typography.fontSizes.md }}>
                            {isTrial ? '7-Day Free Trial' : data.title}
                        </Typography>
                        <Typography variant='body1' sx={{ color: theme.palette.grey[500], fontWeight: 500, fontSize: theme.typography.fontSizes.xs }}>
                            {isTrial ? TRIAL_MOMENTS_LABEL : data.description}
                        </Typography>
                    </StyledTableBodyCell>
                </TableRow>
                <TableRow>
                    <StyledTableHeadCell component="th">Type / Status</StyledTableHeadCell>
                    <StyledTableBodyCell >
                        <Stack gap={2} direction={'row'} flexWrap={'wrap'}>
                            <TypeChip label={data.type} /> / <TypeChip label={data.paymentStatus} />
                        </Stack>
                    </StyledTableBodyCell>
                </TableRow>
                <TableRow>
                    <StyledTableHeadCell component="th">Payment Method</StyledTableHeadCell>
                    <StyledTableBodyCell style={{ textTransform: 'capitalize' }}>
                        {data.paymentMethodType}
                    </StyledTableBodyCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <StyledTableHeadCell component="th">Amount</StyledTableHeadCell>
                    <StyledTableBodyCell sx={{ textAlign: 'right' }}>
                        <Typography>${data?.amount?.toFixed(2)}</Typography>
                        {!isTrial && data?.realAmount != null && data.realAmount !== data?.amount && (
                            <Typography color='textDisabled' fontSize={15} variant='h6' sx={{ textDecoration: 'line-through' }}>
                                ${data.realAmount.toFixed(2)}
                            </Typography>
                        )}
                    </StyledTableBodyCell>
                </TableRow>
            </TableBody>
        </Table>
    </Box>
}
function PaymentHistory() {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
    const { data, isError, isLoading, error, refetch } = useGetGetTransactionHistoryQuery({ page: 1, limit: 50 });
    const { data: subscriptionData } = useGetStudentSubscriptionQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const subscription = subscriptionData?.data?.data;
    const isInTrial =
        subscription?.isInTrial === true ||
        subscription?.trialStatus?.toLowerCase() === 'active';
    const [transactions, setTransactions] = useState<BillingHistory[]>()
    const [isCSVDownload, setCSVDownload] = useState(false)
    useEffect(() => {
        ; (async () => {
            await refetch();
        })()
    }, []);

    // Refetch when subscription changes (e.g. trial → paid) so history stays correct
    useEffect(() => {
        refetch();
    }, [subscription?.isInTrial, subscription?.status]);

    useEffect(() => {
        if (data?.data?.transactions) {
            setTransactions(data?.data?.transactions)
        }
    }, [data])


    const handleSearchTransactions = (searchString: string) => {
        if (searchString) {
            const data = transactions?.filter(item => item.title?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) || item.amount.toString().includes(searchString));
            setTransactions(data);
            return
        }
        setTransactions(data?.data?.transactions)
    }
    const downloadCSVFile = async () => {
        const orderedFields = ["title", "description", "type", "amount", "createdAt", "paymentMethodType", "paymentStatus", "last4Digit"];
        const skipFields = ["isFirstPayment", "paymentId", "subscriptionId", "memberId", "customerId", "paymentMethodId", "_id", "__v", "updatedAt"];
        const headerConversion = {
            type: "Type",
            amount: "Amount",
            createdAt: "Date / Time",
            title: "Title",
            description: "Description",
            last4Digit: "Card Last 4 Digit",
            paymentMethodType: "Payment Method",
            paymentStatus: "Status"
        }
        setCSVDownload(true)
        try {
            await generateJSONToCSV(transactions, orderedFields, skipFields, headerConversion);
            setCSVDownload(false)
        } catch (error) {
            console.log(error);
        }
    }
    return (
        <Box>
            <BlockContainer sx={{ paddingLeft: '10px', paddingRight: '10px' }}>
                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={3} mb={3}>
                    <ListAlt sx={{ color: theme.palette.primary[400] }} />
                    <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600 }}>Recent Transactions</Typography>
                </Stack>
                <Stack direction={'row'} justifyContent={'space-between'} mb={4} flexWrap={'wrap'}
                    alignItems={'center'}
                >
                    <SearchBar onSearch={(value) => handleSearchTransactions(value)} />
                    <DownloadCSVButton onClick={downloadCSVFile} isLoading={isCSVDownload} />
                </Stack>
                {isLargeScreen ? <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <StyledTableHeadCell>Date</StyledTableHeadCell>
                                <StyledTableHeadCell>Description</StyledTableHeadCell>
                                <StyledTableHeadCell>Type</StyledTableHeadCell>
                                <StyledTableHeadCell>Payment Method</StyledTableHeadCell>
                                <StyledTableHeadCell>Status</StyledTableHeadCell>
                                <StyledTableHeadCell sx={{ textAlign: 'right' }}>Amount</StyledTableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions?.length ? transactions.map((transaction: BillingHistory, index: number) => (
                                <TableRow
                                    key={index}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <StyledTableBodyCell component="th" scope="row">
                                        {moment(transaction?.createdAt).format("MMM, DD YYYY")}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell >
                                        <Typography variant='body1' sx={{ color: theme.palette.grey[800], fontWeight: 400, fontSize: theme.typography.fontSizes.sm, textTransform: 'capitalize' }}>
                                            {isTrialTransaction(transaction, isInTrial) ? '7-Day Free Trial' : transaction?.title}
                                        </Typography>
                                        <Typography variant='body1' sx={{ color: theme.palette.grey[500], fontWeight: 400, fontSize: theme.typography.fontSizes.xs, textTransform: 'capitalize' }}>
                                            {isTrialTransaction(transaction, isInTrial) ? TRIAL_MOMENTS_LABEL : transaction?.description}
                                        </Typography>
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell >
                                        <TypeChip label={transaction?.type} />
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell >*****{transaction?.last4Digit}</StyledTableBodyCell>
                                    <StyledTableBodyCell >
                                        <TypeChip label={transaction?.paymentStatus} />
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell sx={{ textAlign: 'right' }}>
                                        <Typography>${transaction?.amount?.toFixed(2)}</Typography>
                                        {!isTrialTransaction(transaction, isInTrial) && transaction?.realAmount != null && transaction.realAmount !== transaction?.amount && (
                                            <Typography color='textDisabled' fontSize={15} variant='h6' sx={{ textDecoration: 'line-through' }}>
                                                ${transaction.realAmount.toFixed(2)}
                                            </Typography>
                                        )}
                                    </StyledTableBodyCell>
                                </TableRow>
                            )) : <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, }}>
                                <StyledTableBodyCell colSpan={6} component="th" scope="row" sx={{ textAlign: 'center' }}>
                                    {isLoading ? <CircularProgress size="30px" /> : <Typography textAlign={'center'} variant='body2' color='textSecondary'>No Transaction found</Typography>}
                                </StyledTableBodyCell>
                            </TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer> : transactions?.length ? transactions.map((transaction: BillingHistory, index: number) => <TableCard data={transaction} isInTrial={isInTrial} key={index} />) :
                    isLoading ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress size={"30px"} /></Box> : <Typography textAlign={'center'} variant='body2' color='textSecondary'>No Transaction found</Typography>
                }
            </BlockContainer>
        </Box>
    )
}

export default PaymentHistory